import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { format } from "date-fns";
import type { NoteAudience } from "@shyne/shared/api";
import { AppointmentsRepository, AppointmentLogJoin } from "@/repositories/appointments.repository";
import { audienceLabel, isNote } from "@/repositories/appointment-history";
import NoteAudiencePicker from "@/components/shared/NoteAudiencePicker";
import { UserRole } from "./actions";
import { uuid } from "@/interfaces";

interface AppointmentCommunicationSheetProps {
  appointmentId: uuid;
  userRole: UserRole;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AppointmentCommunicationSheet({
  appointmentId,
  userRole,
  open,
  onOpenChange,
}: AppointmentCommunicationSheetProps) {
  const [notes, setNotes] = useState<AppointmentLogJoin[]>([]);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [audience, setAudience] = useState<NoteAudience[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isVendor = userRole === UserRole.VENDOR;

  const loadNotes = async () => {
    setLoading(true);
    try {
      const logs = await AppointmentsRepository.listHistory(appointmentId);
      setNotes(logs.filter(isNote));
    } catch (e: any) {
      toast.error(e.message || "Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadNotes();
    }
  }, [open, appointmentId]);

  const handleSubmit = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    try {
      await AppointmentsRepository.addNote(appointmentId, noteText.trim(), isVendor ? audience : undefined);
      toast.success("Note added");
      setNoteText("");
      setAudience([]);
      await loadNotes();
    } catch (e: any) {
      toast.error(e.message || "Failed to add note");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-4 py-4 border-b">
          <SheetTitle>Communication Log</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading && <p className="text-sm text-muted-foreground">Loading...</p>}
          {!loading && notes.length === 0 && (
            <p className="text-sm text-muted-foreground">No notes yet.</p>
          )}
          {notes.map((note) => (
            <div key={note.id} className="border rounded-md p-3 space-y-1.5 bg-muted/30">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  {note.user?.name ?? "Unknown"} · {format(new Date(note.created_at), "MMM d, yyyy h:mm a")}
                </span>
                {note.audience !== undefined && (
                  <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                    {audienceLabel(note.audience)}
                  </Badge>
                )}
              </div>
              <p className="text-sm leading-relaxed">{note.message}</p>
            </div>
          ))}
        </div>

        <div className="border-t px-4 py-4 space-y-3">
          <Textarea
            placeholder="Write a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            className="resize-none"
          />

          {isVendor && <NoteAudiencePicker audience={audience} onChange={setAudience} idPrefix="communication-audience" />}

          <Button
            size="sm"
            disabled={!noteText.trim() || submitting}
            onClick={handleSubmit}
            className="w-full"
          >
            {submitting ? "Adding..." : "Add Note"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
